import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDownIcon } from "../../../icons";
import { mountWithLkid } from "../../../support";
import { useFooter } from "../hooks";
import styles from "./footer-collapsable-desktop.module.css";

export function FooterCollapsableDesktop() {
  const { links, isExpanded, setIsExpanded, handleFooterLinkClick, getLinkTarget } =
    useFooter();
  const ref = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState<number>(0);
  const [dataQaDevice, setDataQaDevice] = useState<string>('');

  const expandedText = useMemo(() => {
    return isExpanded ? "Menos informações" : "Mais informações";
  }, [isExpanded]);

  useEffect(() => {
    const element = ref.current;

    const updateMinHeight = () => {
      setMinHeight(isExpanded ? element?.clientHeight ?? 0 : 0);
    };

    updateMinHeight();
    const resizeObserver = new ResizeObserver(() => {
      updateMinHeight();
    });

    if (element) {
      resizeObserver.observe(element);
      return () => resizeObserver.unobserve(element);
    }
  }, [isExpanded]);

  function handleToggle() {
    setIsExpanded((isExpanded: boolean) => !isExpanded);
  }

  useEffect(() => {
    if (window.innerWidth < 842) {
      setDataQaDevice('-mobile');
    }
  }, [])

  return (
    <>
      <button
        type="button"
        onClick={handleToggle}
        className={styles.ExpandButton}
        data-qa={`footer_${isExpanded ? "menosInformacoes" : "maisInformacoes"}${dataQaDevice}`}
      >
        {expandedText}
        <ArrowDownIcon
          className={isExpanded ? styles.Rotate_180 : styles.Rotate_0}
          color="#fff"
          size={24}
        />
      </button>
      <nav
        aria-label="Seções principais do site"
        className={styles.Collapse}
        style={{ minHeight }}
      >
        <div ref={ref} className={styles.Table}>
          <div className={styles.Column}>
            <section className={styles.Row}>
              <h2 className={styles.Title}>Comprar</h2>
              <ul>
                {Object.values(links?.BUY || {}).map((link) => {
                  const href = mountWithLkid(link);
                  return (
                    <li key={link.id}>
                      <a
                        id={link.id}
                        className={styles.Link}
                        href={href}
                        data-testid={link.testId}
                        {...getLinkTarget(href)}
                        data-qa={link.dataQA}
                      >
                        {link.text}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
            <section className={styles.Row}>
              <h2 className={styles.Title}>Vender</h2>
              <ul>
                {Object.values(links.SELL).map((link) => {
                  const href = mountWithLkid(link);
                  return (
                    <li key={link.id}>
                      <a
                        id={link.id}
                        className={styles.Link}
                        href={href}
                        data-testid={link.testId}
                        {...getLinkTarget(href)}
                        data-qa={link.dataQA}
                      >
                        {link.text}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>

          <div className={styles.Column}>
            {links.SUBSCRIPTION && (
              <section className={styles.Row}>
                <h2 className={styles.Title}>Assinar</h2>
                <ul>
                  {Object.values(links.SUBSCRIPTION).map((link) => {
                    const href = mountWithLkid(link);
                    return (
                      <li key={link.id}>
                        <a
                          id={link.id}
                          className={styles.Link}
                          href={href}
                          data-testid={link.testId}
                          {...getLinkTarget(href)}
                        >
                          {link.text}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
            <section className={styles.Row}>
              <h2 className={styles.Title}>Serviços</h2>
              <ul>
                {Object.values(links.SERVICES).map((link) => {
                  const href = mountWithLkid(link);
                  return (
                    <li key={link.id}>
                      <a
                        id={link.id}
                        className={styles.Link}
                        href={href}
                        data-testid={link.testId}
                        {...getLinkTarget(href)}
                        data-qa={link.dataQA}
                      >
                        {link.text}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>
          <div className={styles.Column}>
            <section className={styles.Row}>
              <h2 className={styles.Title}>Notícias WM1</h2>
              <ul>
                {Object.values(links.NEWS).map((link) => {
                  const href = mountWithLkid(link);
                  return (
                    <li key={link.id}>
                      <a
                        id={link.id}
                        className={styles.Link}
                        href={href}
                        data-testid={link.testId}
                        {...getLinkTarget(href)}
                        data-qa={link.dataQA}
                      >
                        {link.text}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>
          <div className={styles.Column}>
            <section className={styles.Row}>
              <h2 className={styles.Title}>Ajuda</h2>
              <ul>
                {Object.values(links.HELP).map((link) => {
                  const href = mountWithLkid(link);
                  return (
                    <li key={link.id}>
                      <a
                        id={link.id}
                        className={styles.Link}
                        href={href}
                        data-testid={link.testId}
                        {...getLinkTarget(href)}
                        data-qa={link.dataQA}
                      >
                        {link.text}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>
          <div className={styles.Column}>
            <section className={styles.Row}>
              <h2 className={styles.Title}>Institucional</h2>
              <ul>
                {Object.values(links.INSTITUTIONAL).map((link) => {
                  const href = mountWithLkid(link);
                  return (
                    <li key={link.id}>
                      <a
                        id={link.id}
                        className={styles.Link}
                        href={href}
                        data-testid={link.testId}
                        {...getLinkTarget(href)}
                        data-qa={link.dataQA}
                        onClick={(event) => {
                          handleFooterLinkClick(event, link.id);
                        }}
                      >
                        {link.text}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>
        </div>
      </nav>
    </>
  );
}
